import { createEnv } from '@t3-oss/env-core';
import { z } from 'zod';

// Inventory + schema decisions:
// - VITE_API_ORIGIN: api-client.ts uses `import.meta.env.VITE_API_ORIGIN ?? ''`
//   In dev, Vite proxies /api to localhost:3000 so the origin can be empty
//   (same-origin). In prod, the reverse-proxy handles it; override via .env.local.
//   Default: '' (empty string = same-origin, matches the ?? '' fallback).
// - VITE_FINANCE_WS_BASE: finance-stream.ts reads
//   `(import.meta.env.VITE_FINANCE_WS_BASE as string | undefined) ?? 'ws://localhost:3005'`
//   Points at svc-finance's WebSocket endpoint. Dev default matches the local
//   docker-compose port. Plain z.string() (not .url()) because z does not
//   validate ws:// / wss:// schemes.
//   Default: 'ws://localhost:3005'.
// - VITE_PLAYER_APP_URL: backoffice login-as-member opens the player web app at
//   this origin (`${url}/login/impersonate#data=...`). Dev default matches the
//   player lobby port (§7.4). v1 is same-brand only — a single global player URL.
//   Default: 'http://localhost:5175'.
// - VITE_MOCK_BANK_URL: origin of the mock Lao bank app (apps/web/mock-catnap24,
//   Procfile port 7200), which the /tools sandbox drives from the browser.
//   An OVERRIDE, not a switch: `mock-bank.ts` already defaults to that port in a
//   dev build, so set this only when the mock runs somewhere else. What decides
//   whether the tools page exists at all is `isDevBuild()` below.
//   Plain z.string(), not .url(): '' is a real value and .url() would refuse it.
//   Default: '' (fall back to the dev default, or nothing in a built bundle).
// - VITE_ENABLE_DEV_TOOLS: 'true' lets a BUILT bundle offer the root-only /tools
//   page (see `devToolsEnabled()`). Set by the dev Pages deploy only
//   (.gitlab/ci/frontend-pages.yml); `dev-tools-flag.test.ts` fails if the prod
//   deploy ever sets it.
//   Default: 'false'.
// - VITE_ART_READ_ONLY: 'true' makes game art read only in this bundle: the art
//   pages and the game form's pin switch offer no changes and say they are made
//   in the production backoffice (ADR-0022; svc-art and svc-game refuse them
//   from dev anyway). Set by the dev Pages deploy only
//   (.gitlab/ci/frontend-pages.yml); `art-read-only-flag.test.ts` fails if the
//   prod deploy ever sets it.
//   Default: 'false'.
export const env = createEnv({
  clientPrefix: 'VITE_',
  client: {
    VITE_API_ORIGIN: z.string().default(''),
    VITE_FINANCE_WS_BASE: z.string().default('ws://localhost:3005'),
    VITE_PLAYER_APP_URL: z.string().url().default('http://localhost:5175'),
    VITE_MOCK_BANK_URL: z.string().default(''),
    VITE_ENABLE_DEV_TOOLS: z.enum(['true', 'false']).default('false'),
    VITE_ART_READ_ONLY: z.enum(['true', 'false']).default('false'),
  },
  runtimeEnv: import.meta.env,
  emptyStringAsUndefined: true,
});

export type Env = typeof env;

/**
 * True only under `vite dev`.
 *
 * `import.meta.env.DEV` is one of the two Vite built-ins CLAUDE.md 3.10 allows to
 * be read directly, and it is false in EVERY built bundle — dev cluster and prod
 * Pages alike. That is what makes it the kill switch for anything that must not
 * reach a deployment.
 *
 * It is a function, not a const, for two reasons: outside Vite (`bun test`) the
 * whole `import.meta.env` object is undefined, so the comparison has to be
 * explicit; and a call site is something a test can stub, which a value inlined
 * at module scope is not.
 */
export function isDevBuild(): boolean {
  return import.meta.env.DEV === true;
}

/**
 * Whether operator dev tooling (/tools) may exist in this bundle: always under
 * `vite dev`, and in a built bundle only when the deploy opted in with
 * `VITE_ENABLE_DEV_TOOLS` (the dev cluster). Callers still require root.
 */
export function devToolsEnabled(): boolean {
  return isDevBuild() || env.VITE_ENABLE_DEV_TOOLS === 'true';
}
